import React, {useContext, useMemo} from 'react'
import ScrollBar from 'react-perfect-scrollbar'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {updateRef} from '@containers/element-utils'
import {useBoolean} from '@containers/hooks'
import {Padding} from '../design-system/types'
import {TwCnt, TwDivider} from './containers'
import {PropsContext} from './context'
import {DividerVisibility} from './types'

export const PopupContent = ({
  dividerVisibility,
  sidebar,
  paddings,
  scrollBarRef,
  containerRef
}: {
  dividerVisibility: DividerVisibility
  sidebar?: React.ReactNode
  paddings?: Padding
  scrollBarRef?: React.ForwardedRef<ScrollBar>
  containerRef?: React.Ref<HTMLElement>
}) => {
  const props = useContext(PropsContext)
  const {children, contentClassName, noScroll = false} = props

  const scrolledDown = useBoolean(false)
  const onScroll = (container: HTMLElement) => {
    if ((container.scrollTop !== 0) !== scrolledDown.value) {
      scrolledDown.setValue(container.scrollTop !== 0)
    }
  }

  const noContent = useMemo(() => {
    if (Array.isArray(children)) {
      return children.filter(Boolean).length === 0
    }
    return !children
  }, [children])

  const container = !noContent && (
    <TwCnt $paddings={paddings} className={contentClassName}>
      {children}
    </TwCnt>
  )

  const hasSidebar = Boolean(sidebar)
  const content = noScroll ? (
    container
  ) : (
    <WithPerfectScrollbar
      ref={x => updateRef(x, scrollBarRef)}
      containerRef={x => updateRef(x, containerRef)}
      onScrollY={onScroll}
      className="h-auto flex-auto"
    >
      {container}
    </WithPerfectScrollbar>
  )

  const isDividerVisible =
    (dividerVisibility === 'auto' && scrolledDown.value) ||
    dividerVisibility === 'visible'

  return (
    <>
      <TwDivider
        $style={dividerVisibility}
        style={{visibility: isDividerVisible ? 'visible' : 'hidden'}}
      />

      {hasSidebar ? (
        <div className="flex overflow-hidden h-full w-full">
          <div className="shrink-0">{sidebar}</div>
          {content}
        </div>
      ) : (
        content
      )}
    </>
  )
}
